import React from 'react';
import {
  FiCamera, FiCalendar, FiMail, FiPhone, FiCopy, FiAtSign,
  FiCheckCircle, FiSlash, FiRotateCcw, FiPackage,
} from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { ROL_LABELS, ESTADO_LABELS_LARGO, DEPTO_LABELS, esStaff } from '../../../../constants/usuarios';

import i18n from '../../../../i18n';

const formatearAlta = (fecha) => (fecha
  ? new Date(fecha).toLocaleDateString(i18n.language, { day: 'numeric', month: 'long', year: 'numeric' })
  : '—');

/** Fila de contacto: rótulo, valor y copia rápida al portapapeles. */
const FilaContacto = ({ icono: Icono, label, valor, href, vacio, onCopiar, tCopiar }) => (
  <div className="pu-contacto-fila">
    <span className="pu-contacto-icono"><Icono /></span>
    <span className="pu-contacto-texto">
      <span className="pu-contacto-label">{label}</span>
      {valor ? (
        <span className="pu-contacto-valor">
          {href ? <a href={href} title={label}>{valor}</a> : valor}
        </span>
      ) : (
        <span className="pu-contacto-valor pu-contacto-valor--vacio">{vacio}</span>
      )}
    </span>
    {valor && onCopiar && (
      <button
        type="button"
        className="pu-copiar"
        onClick={() => onCopiar(valor, tCopiar)}
        title={tCopiar}
        aria-label={tCopiar}
      >
        <FiCopy />
      </button>
    )}
  </div>
);

/**
 * Tarjeta vertical de identidad: quién es, cómo contactarle, en qué estado
 * está su cuenta y qué tiene contratado. Reúne lo que antes se repartía entre
 * la cabecera y las tarjetas laterales.
 */
const TarjetaIdentidad = ({
  usuarioData,
  fotoActual,
  subiendoFoto,
  onCambiarFoto,
  puedeCambiarEstado,
  cambiandoEstado,
  onCambiarEstado,
  onCopiar,
  servicios,
  cargandoServicios,
  serviciosPendientes,
}) => {
  const { t } = useTranslation();
  const estadoActual = usuarioData.estado || 'verificado';
  const totalHoras = servicios.reduce((acc, s) => acc + (s.horas || 0), 0);

  return (
    <section className={`pu-identidad pu-identidad--${estadoActual}`}>
      <div className="pu-identidad-cuerpo">
        <div className="pu-avatar-wrap">
          <img className="pu-avatar" src={fotoActual} alt={`${t('ticket.avatarDe', { nombre: usuarioData.nombre })}`} />
          <button
            type="button"
            className="pu-avatar-btn"
            onClick={onCambiarFoto}
            disabled={subiendoFoto}
            title={t('perfilUsuario.cambiarFotoPerfil')}
            aria-label={t('perfilUsuario.cambiarFotoPerfil')}
          >
            {subiendoFoto
              ? <span className="btn-spinner" style={{ borderTopColor: '#fff', width: 12, height: 12 }} />
              : <FiCamera />}
          </button>
        </div>

        <h1 className="pu-identidad-nombre">{usuarioData.nombre} {usuarioData.apellidos}</h1>

        <button
          type="button"
          className="pu-identidad-usuario"
          onClick={() => onCopiar(usuarioData.usuario, t('perfilUsuario.usuarioCopiado'))}
          title={t('perfilUsuario.copiarUsuario')}
        >
          <FiAtSign />{usuarioData.usuario}
        </button>

        {/* Rol y estado: el color va en el punto del badge, sin icono que lo repita */}
        <div className="pu-badges">
          <span className={`pu-badge pu-badge--rol-${usuarioData.role}`}>
            {ROL_LABELS[usuarioData.role] || t('comun.usuario')}
          </span>
          <span className={`pu-badge pu-badge--estado-${estadoActual}`}>
            {ESTADO_LABELS_LARGO[estadoActual]}
          </span>
          {esStaff(usuarioData.role) && (
            <span className="pu-badge pu-badge--dept">
              {DEPTO_LABELS[usuarioData.dpto_tecnico] || t('perfilUsuario.sinDepartamento')}
            </span>
          )}
        </div>

        <div className="pu-contacto">
          <FilaContacto
            icono={FiMail}
            label={t('comun.email')}
            valor={usuarioData.email}
            href={usuarioData.email ? `mailto:${usuarioData.email}` : null}
            vacio={t('perfilUsuario.sinEmail')}
            onCopiar={onCopiar}
            tCopiar={t('comun.copiar') + ' ' + t('comun.email').toLowerCase()}
          />
          <FilaContacto
            icono={FiPhone}
            label={t('comun.telefono')}
            valor={usuarioData.telefono}
            href={usuarioData.telefono ? `tel:${usuarioData.telefono}` : null}
            vacio={t('perfilUsuario.sinTelefono')}
            onCopiar={onCopiar}
            tCopiar={t('comun.copiar') + ' ' + t('comun.telefono').toLowerCase()}
          />
          <FilaContacto
            icono={FiCalendar}
            label={t('proyectos.inicio')}
            valor={formatearAlta(usuarioData.createdAt)}
          />
        </div>

        {puedeCambiarEstado && (
          <div className="pu-identidad-acciones">
            {estadoActual !== 'verificado' && (
              <button
                type="button"
                className="pu-btn-accion pu-btn-accion--ok"
                onClick={() => onCambiarEstado('verificado')}
                disabled={cambiandoEstado}
              >
                {estadoActual === 'suspendido' ? <><FiRotateCcw /> {t('menu.reactivar')}</> : <><FiCheckCircle /> {t('menu.verificar')}</>}
              </button>
            )}
            {estadoActual !== 'suspendido' && (
              <button
                type="button"
                className="pu-btn-accion pu-btn-accion--peligro"
                onClick={() => onCambiarEstado('suspendido')}
                disabled={cambiandoEstado}
              >
                <FiSlash /> {t('menu.suspender')}
              </button>
            )}
          </div>
        )}

        {/* Servicios: aquí solo se consultan; se editan en Ajustes avanzados */}
        <div className="pu-identidad-bloque">
          <p className="pu-bloque-titulo">
            <FiPackage /> {t('perfilUsuario.serviciosAsignados')}
            {totalHoras > 0 && <b>{totalHoras} h</b>}
          </p>

          {cargandoServicios ? (
            <div className="pu-skeleton" aria-hidden="true"><span /><span /></div>
          ) : servicios.length === 0 ? (
            <p className="pu-vacio-linea">{t('perfilUsuario.sinServiciosAsignados')}</p>
          ) : (
            <div className="pu-chips">
              {servicios.map((servicio) => (
                <span key={servicio.id} className={`pu-chip pu-chip--${servicio.categoria}`}>
                  {servicio.titulo}
                  {servicio.horas ? <span className="pu-chip-horas">{servicio.horas} h</span> : null}
                </span>
              ))}
            </div>
          )}

          {serviciosPendientes && (
            <p className="pu-vacio-linea" style={{ marginTop: 8 }}>{t('perfilUsuario.cambiosSinGuardarServicios')}</p>
          )}
        </div>
      </div>
    </section>
  );
};

export default TarjetaIdentidad;
